import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFloorplanViewState } from 'floorplan/pages/native-floorplan/hooks/use-floorplan-view-state.hook';
import { CoworkerConfigFlags, selectCoworkerConfigFlags } from 'store/floorplan-app-instance-config/selectors';
import { kioskEnabled } from 'store/kiosk/selectors';

export type CoworkerBlockedState = 'visibilityOff' | 'companyOnly' | 'teamsOff';

export const COWORKER_BLOCKED_STATE_MESSAGE_ID: Record<CoworkerBlockedState, string> = {
  visibilityOff: 'coworkerVisibilityOff',
  companyOnly: 'coworkerCompanyOnly',
  teamsOff: 'coworkerTeamsOff',
};

export interface CoworkerVisibility {
  effectiveName: boolean;
  effectiveCompany: boolean;
  effectiveTeams: boolean;
  isCoworkerLayerEnabled: boolean;
  blockedState: CoworkerBlockedState | null;
  isKioskMode: boolean;
}

interface CoworkerVisibilityContext {
  floorplanPublished: boolean;
  isKioskMode: boolean;
}

const KIOSK_VISIBILITY: CoworkerVisibility = {
  effectiveName: false,
  effectiveCompany: false,
  effectiveTeams: false,
  isCoworkerLayerEnabled: false,
  blockedState: null,
  isKioskMode: true,
};

export const resolveCoworkerVisibility = (
  flags: CoworkerConfigFlags,
  { floorplanPublished, isKioskMode }: CoworkerVisibilityContext,
): CoworkerVisibility => {
  if (isKioskMode) {
    return KIOSK_VISIBILITY;
  }

  const effectiveName = flags.nameRevealed;
  const effectiveCompany = flags.companyRevealed;
  // Teams are a by-name feature (§0.1: effectiveTeams = effectiveName && …): the selector already
  // folds the name into `teamsConfigured`, and the invariant is enforced here as well so no
  // consumer can ever see teams without names, whatever flags it is handed.
  const effectiveTeams = effectiveName && flags.teamsConfigured && floorplanPublished;
  const isCoworkerLayerEnabled = effectiveName || effectiveCompany;

  return {
    effectiveName,
    effectiveCompany,
    effectiveTeams,
    isCoworkerLayerEnabled,
    blockedState: resolveBlockedState({ effectiveName, effectiveTeams, isCoworkerLayerEnabled }),
    isKioskMode: false,
  };
};

const resolveBlockedState = ({
  effectiveName,
  effectiveTeams,
  isCoworkerLayerEnabled,
}: Pick<
  CoworkerVisibility,
  'effectiveName' | 'effectiveTeams' | 'isCoworkerLayerEnabled'
>): CoworkerBlockedState | null => {
  if (!isCoworkerLayerEnabled) {
    return 'visibilityOff';
  }

  if (!effectiveName) {
    return 'companyOnly';
  }

  return effectiveTeams ? null : 'teamsOff';
};

export const useCoworkerVisibility = (): CoworkerVisibility => {
  const flags = useSelector(selectCoworkerConfigFlags);
  const isKioskMode = useSelector(kioskEnabled);
  const { isFloorplanAvailable } = useFloorplanViewState();

  return useMemo(
    () => resolveCoworkerVisibility(flags, { floorplanPublished: isFloorplanAvailable, isKioskMode }),
    [flags, isFloorplanAvailable, isKioskMode],
  );
};
